import { Building2, ArrowRight, Shield, Users, Zap, Clock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const Sobre = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <Building2 className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">itaCondo</h1>
                <p className="text-xs text-slate-500">Gestão Residencial</p>
              </div>
            </Link>
            
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="outline">Voltar ao Início</Button>
              </Link>
              <Link to="/login">
                <Button>Entrar</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Two Column Layout */}
      <section className="py-16 lg:py-24 bg-white overflow-hidden relative">
        {/* Floating Badge - Online 24/7 - Top Right of Section */}
        <div className="absolute top-8 right-4 lg:right-12 z-20 animate-float">
          <div className="bg-white rounded-xl px-4 py-3 shadow-lg border border-slate-200 flex items-center gap-3">
            <div className="relative">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <div className="w-3 h-3 bg-green-500 rounded-full absolute top-0 left-0 animate-ping"></div>
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Online 24/7</div>
              <div className="text-xs text-slate-500">Always available</div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-100 text-cyan-700 text-sm font-medium">
                ABOUT ITACONDO
              </div>

              {/* Main Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Transforming businesses through{" "}
                <span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">
                  innovative technology
                </span>
              </h1>

              {/* Paragraphs */}
              <div className="space-y-4">
                <p className="text-lg text-slate-600 leading-relaxed">
                  We are a team of passionate developers, designers, and strategists 
                  dedicated to creating cutting-edge solutions that help businesses 
                  thrive in the digital age.
                </p>
                <p className="text-lg text-cyan-600 leading-relaxed">
                  Our mission is to deliver exceptional software products that combine 
                  beautiful design with powerful functionality.
                </p>
              </div>

              {/* Stats Grid - Icons above numbers */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-8">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 text-center">
                  <FileText className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">500+</div>
                  <div className="text-sm text-slate-500">Projects Delivered</div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 text-center">
                  <Users className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">10K+</div>
                  <div className="text-sm text-slate-500">Users Served</div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 text-center">
                  <Clock className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">99.9%</div>
                  <div className="text-sm text-slate-500">Uptime</div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 text-center">
                  <Zap className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">24/7</div>
                  <div className="text-sm text-slate-500">Support</div>
                </div>
              </div>
            </div>

            {/* Right Column - Visual Card */}
            <div className="relative flex justify-center lg:justify-end">
              {/* Main Card */}
              <div className="relative w-full max-w-md">
                <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 rounded-3xl p-8 lg:p-12 shadow-2xl">
                  {/* Card Content */}
                  <div className="flex flex-col items-center text-center space-y-6">
                    {/* Icon */}
                    <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <FileText className="w-10 h-10 text-white" />
                    </div>

                    {/* Title */}
                    <div>
                      <h2 className="text-3xl font-bold text-white tracking-wider">ITACONDO</h2>
                      <p className="text-cyan-400 text-lg mt-2">Technology Solutions</p>
                    </div>

                    {/* Decorative Lines */}
                    <div className="w-full space-y-3 pt-4">
                      <div className="h-2 bg-slate-700/50 rounded-full w-full"></div>
                      <div className="h-2 bg-slate-700/50 rounded-full w-3/4"></div>
                      <div className="h-2 bg-slate-700/50 rounded-full w-1/2"></div>
                    </div>
                  </div>
                </div>

                {/* Floating Badge - Secure & Reliable - Bottom Center of Card */}
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 z-20 animate-float-delayed">
                  <div className="bg-white rounded-xl px-4 py-3 shadow-lg border border-slate-200 flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Secure & Reliable</div>
                      <div className="text-xs text-slate-500">Enterprise-grade security</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-blue-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-cyan-100 mb-8">
              Join thousands of satisfied customers who trust ItaCondo for their technology needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login">
                <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button size="lg" variant="outline" className="text-lg px-8 py-4 bg-transparent text-white border-white hover:bg-white/10">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="text-white font-bold">itaCondo</span>
            </div>
            <p className="text-slate-400 mb-4">
              Sistema completo de gestão residencial para condomínios modernos.
            </p>
            <p className="text-slate-400">
              © 2024 itaCondo. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Sobre;
